import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel, FieldSet, FieldLegend } from '@/components/ui/field';
import { birthRuleParameters, birthRuleTerms, type BirthRuleConfiguration, type BirthRule } from './birthRules';
export function BirthRuleConfigEditor({
  value,
  onChange,
}: {
  value?: BirthRuleConfiguration;
  onChange: (value: BirthRuleConfiguration) => void;
}) {
  const current = value ?? { clinically_reviewed: false, rules: [], timers: [] };
  const update = (index: number, patch: Partial<BirthRule>) =>
    onChange({
      ...current,
      clinically_reviewed: false,
      rules: current.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    });
  return (
    <FieldSet>
      <FieldLegend>Labor, postpartum and newborn alerts</FieldLegend>
      <p className="text-sm text-muted-foreground">
        No clinical thresholds are preselected. Enter the protocol values and source, then document review before
        enabling alerts.
      </p>
      {current.rules.map((rule, index) => (
        <FieldSet key={rule.id}>
          <FieldLegend>{rule.id}</FieldLegend>
          <Field orientation="horizontal">
            <Checkbox
              id={`birth-${rule.id}-enabled`}
              checked={rule.enabled}
              onCheckedChange={(v) => update(index, { enabled: v === true })}
            />
            <FieldLabel htmlFor={`birth-${rule.id}-enabled`}>Enable rule</FieldLabel>
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            {['message', 'citation'].map((key) => (
              <Field key={key}>
                <FieldLabel htmlFor={`birth-${rule.id}-${key}`}>
                  {key === 'message' ? 'Alert wording' : 'Source / citation'}
                </FieldLabel>
                <Input
                  className="min-h-11"
                  id={`birth-${rule.id}-${key}`}
                  value={rule[key as 'message' | 'citation']}
                  onChange={(e) => update(index, { [key]: e.target.value })}
                />
              </Field>
            ))}
            {(birthRuleParameters[rule.id] ?? []).map((key) => (
              <Field key={key}>
                <FieldLabel htmlFor={`birth-${rule.id}-${key}`}>{key.replaceAll('_', ' ')}</FieldLabel>
                <Input
                  className="min-h-11"
                  id={`birth-${rule.id}-${key}`}
                  type="number"
                  step="any"
                  value={Number.isFinite(rule.params[key]) ? rule.params[key] : ''}
                  onChange={(e) =>
                    update(index, {
                      params: { ...rule.params, [key]: e.target.value === '' ? Number.NaN : Number(e.target.value) },
                    })
                  }
                />
              </Field>
            ))}
            {(birthRuleTerms[rule.id] ?? []).map((key) => (
              <Field key={key}>
                <FieldLabel htmlFor={`birth-${rule.id}-${key}`}>{key} (comma separated)</FieldLabel>
                <Input
                  className="min-h-11"
                  id={`birth-${rule.id}-${key}`}
                  value={rule.terms?.[key]?.join(', ') ?? ''}
                  onChange={(e) =>
                    update(index, {
                      terms: {
                        ...rule.terms,
                        [key]: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      },
                    })
                  }
                />
              </Field>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            className="min-h-11 self-start"
            onClick={() =>
              onChange({ ...current, clinically_reviewed: false, rules: current.rules.filter((_, i) => i !== index) })
            }>
            Remove {rule.id}
          </Button>
        </FieldSet>
      ))}
      <div className="flex flex-wrap gap-2">
        {Object.keys(birthRuleParameters)
          .filter((id) => !current.rules.some((rule) => rule.id === id))
          .map((id) => (
            <Button
              type="button"
              variant="outline"
              className="min-h-11"
              key={id}
              onClick={() =>
                onChange({
                  ...current,
                  clinically_reviewed: false,
                  rules: [...current.rules, { id, enabled: false, citation: '', message: '', params: {} }],
                })
              }>
              Configure {id}
            </Button>
          ))}
      </div>
      <Field orientation="horizontal">
        <Checkbox
          id="birth-rules-reviewed"
          checked={current.clinically_reviewed}
          onCheckedChange={(v) => onChange({ ...current, clinically_reviewed: v === true })}
        />
        <FieldLabel htmlFor="birth-rules-reviewed">
          I reviewed these clinical alert protocols and their sources.
        </FieldLabel>
      </Field>
    </FieldSet>
  );
}
